import { RadioGroup, FormControlLabel, Radio } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

export function SensitivityAnnotationsEditor({
  open,
  onClose,
  entities,
}: {
  open: boolean;
  onClose: () => void;
  entities: InventoryResource[];
}) {
  const { snackbar } = useWorkspace();
  const [hasSensitivityAnnotations, setHasSensitivityAnnotations] = useState<
    boolean | null
  >(null);
  const dal = useDAL();
  const [updating, setUpdating] = useState(false);

  const onSave = () => {
    if (hasSensitivityAnnotations === null) return;
    setUpdating(true);
    void Promise.all(
      entities.map((entity) =>
        dal.inventory.sensitivityAnnotations.override(
          entity.id,
          hasSensitivityAnnotations
        )
      )
    )
      .then(() => {
        snackbar.showMessage(
          `Sensitivity annotations overridden for ${entities.map((x) => x.resourceName).join(', ')}.`
        );
      })
      .finally(() => {
        setUpdating(false);
        onClose();
      });
  };

  return (
    <MainDialog
      header={{
        title: 'Override sensitivity annotations',
        subtitle:
          "Eon won't automatically detect sensitivity annotations for the selected resources. This could change the applied backup policies and controls.",
        onClose,
      }}
      footer={{
        primaryButton: {
          'data-testid': 'override-button',
          text: 'Override',
          onClick: onSave,
          disabled: hasSensitivityAnnotations === null || updating,
        },
        secondaryButton: {
          'data-testid': 'cancel-button',
          text: 'Cancel',
          onClick: onClose,
        },
      }}
      isOpen={open}
      isLoading={false}
    >
      <RadioGroup>
        <FormControlLabel
          className='w-full'
          control={
            <Radio
              sx={{ padding: '10px 10px 10px 30px' }}
              size='small'
              checked={hasSensitivityAnnotations === true}
              onChange={() => setHasSensitivityAnnotations(true)}
            />
          }
          label='Mark as sensitive'
        />
        <FormControlLabel
          className='w-full'
          control={
            <Radio
              sx={{ padding: '10px 10px 10px 30px' }}
              size='small'
              checked={hasSensitivityAnnotations === false}
              onChange={() => setHasSensitivityAnnotations(false)}
            />
          }
          label='Mark as not sensitive'
        />
      </RadioGroup>
    </MainDialog>
  );
}
